/* eslint-disable css-modules/no-unused-class */
import { useState, useEffect } from 'react';

// jl-design-system
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { GiftCardVoucherProps } from 'types/GiftCardVoucher.types';
import ApplyGenericGiftOptionProps from './ApplyGenericGiftOption.types';

// components
import HiddenAlert from '@widgets/accessibility/hiddenalert';
import GiftOptionsForm from '@components/gift-options-forms/GiftOptionsForm';
import Container from '@components/container';
import PaymentDeductionsList from '@components/payment-deductions/PaymentDeductionsList';
import { ANIMATION_TYPES } from '@constants/giftOptionsConstants';
import errorMessageBodyLookup from '@utils/error/errorMessageBodyLookup';

// styles
import styles from './apply-gift-options-forms.scss';

export const getNonRemovedItems = (items: GiftCardVoucherProps[]) =>
  items?.filter((item: GiftCardVoucherProps) => !item.isRemoved) ?? [];

const ApplyGenericGiftOption = ({
  applyGiftPaymentFailed,
  cardImage,
  doneButtonDataTest,
  doneButtonText,
  errorMessage,
  formConfig,
  formContainerClassName,
  formDataTest,
  formSubmitFailed,
  hiddenAlertSubmittingText = '',
  instructionalText,
  instructionalTextDataTest,
  isLoading,
  isZeroAmount,
  items = [],
  onDoneClick,
  onFormSubmit,
  onRemove,
  onUndo,
  outstandingBalance,
  removeGiftOptionLoading,
  type,
}: ApplyGenericGiftOptionProps) => {
  const [animation, setAnimation] = useState<string | undefined>(undefined);
  const [list, setList] = useState<any>([]);
  const [oldPropsItems, setOldPropsItems] = useState<any>([]);

  useEffect(() => {
    if (isLoading || !items.length) return;

    const appliedNewItems = getNonRemovedItems(items);
    const appliedOldItems = getNonRemovedItems(oldPropsItems);

    let animation = undefined;

    if (oldPropsItems.length === 0 && appliedNewItems.length === 1) {
      animation = ANIMATION_TYPES.WHOLE_SECTION;
    } else if (appliedNewItems.length > appliedOldItems.length) {
      animation = ANIMATION_TYPES.NEW_ITEM;
    }

    setOldPropsItems(items);
    setList(items.reverse());
    setAnimation(animation);
  }, [isLoading, items]);

  return (
    <div className={styles.applyGiftContainer}>
      {
        isLoading &&
        <HiddenAlert text={hiddenAlertSubmittingText} />
      }

      {errorMessage && (
        <Container marginBottom="2">
          <Message
            body={errorMessageBodyLookup({
              body: errorMessage.body,
              bodyLookup: errorMessage.bodyLookup,
            })}
            title={errorMessage.message}
            type="error"
          />
        </Container>
      )}

      <p
        className={styles.introCopy}
        data-testid={instructionalTextDataTest}
      >
        {instructionalText}
      </p>

      <div className={styles.content}>
        <div className={styles.imageWrapper}>
          <img alt={instructionalText} className={styles.image} src={cardImage} />
        </div>

        <div className={styles.formContainer}>
          <GiftOptionsForm
            config={formConfig}
            dataTest={formDataTest}
            disabled={isZeroAmount || removeGiftOptionLoading || isLoading}
            formContainerClassName={formContainerClassName}
            formSubmitFailed={formSubmitFailed}
            isLoading={isLoading}
            onFormSubmit={onFormSubmit}
          />

          <PaymentDeductionsList
            animation={animation}
            applyGiftPaymentFailed={applyGiftPaymentFailed}
            balance={outstandingBalance}
            isRemovable
            list={list}
            onRemove={onRemove}
            onUndo={onUndo}
            showUndo
            type={type}
          />

          <PrimaryButton
            className={styles.doneButton}
            data-testid={doneButtonDataTest}
            disabled={isLoading || removeGiftOptionLoading}
            onClick={onDoneClick}
          >
            {doneButtonText}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default ApplyGenericGiftOption;
